<template>
  <div style="position:absolute;top:0;left:0;right:0;bottom:0;" class="layout-column">
    <div class="layout-column flex ma-2 elevation-5"
         style="border-radius:4px;position:relative;"
         v-bind:style="{background: compColor.contentFrame}">

      <ValidationObserver ref="PhysicianDetailForm" v-slot="{ valid, pristine, reset }" class="layout-column flex">
        <div v-bind:style="{'z-index': '4'}" class="layout-row layout-align-center-center pa-1" v-if="!physicianId || model.pending">
          <div class="layout-row">
            <lx-btn small
                    classes="mr-1"
                    :disabled="pristine"
                    @click="clearForm(reset)"
                    icon="mdi-undo-variant"
                    tooltip="Änderungen am Arzt zurücksetzen"
                    label="Zurücksetzen"
                    hide-label></lx-btn>
            <lx-btn small
                    classes="mr-1"
                    :disabled="!valid || pristine"
                    @click="savePhysicianData"
                    icon="save"
                    :tooltip="physicianId ? 'Änderungen am Arzt speichern' : 'Neuen Arzt anlegen'"
                    label="Speichern"
                    hide-label></lx-btn>
          </div>
          <div class="flex layout-row"></div>
        </div>

        <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

        <div class="flex layout-column pa-2" style="overflow-y: auto;">
          <v-progress-linear :indeterminate="loading" v-if="loading" color="red accent-2"
                             background-color="red lighten-4" class="ma-0"
                             style="border-radius:0;height:4px;position: absolute;top:56px;left:0; width:100%;"></v-progress-linear>

          <lx-expansion-panels v-model="panel">
            <lx-expansion-panel disabled>
              <lx-expansion-panel-header icon="mdi-account-circle"
                                         disabled
                                         :main-headline="$t('COMPONENT_ACCOUNT.HEADER')"
                                         :sub-headline="$t('COMPONENT_ACCOUNT.BASE_DATA_SUB_LINE')">
              </lx-expansion-panel-header>

              <lx-expansion-panel-content>
                <div class="px-4 pt-4 layout-row layout-wrap">
                  <div class="layout-row layout-wrap flex-100">

                    <!-- email -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" ref="email" name="email"
                                           rules="required|email"
                                           v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          v-model="model.email"
                          :label="$t('COMPONENT_ACCOUNT.EMAIL')"
                          :hint="$t('COMPONENT_USER_DETAILS.CHECK_MAIL_HINT')"
                          :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                          :disabled="!!physicianId || !!(authUserExists === false && localUserExists === false && model.email)"
                          :error-messages="errors[0]"
                          :dense="denseFields"
                          :persistent-hint="!physicianId"
                          outlined
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- Button to check mail of new user, will not be shown when existing user  -->

                    <lx-btn :dark="compColor.isInDarkMode" class="white--text text-none ml-2 pa-5" :color="compColor.primeColor"
                            :disabled="!model.email || isMailInvalid || !!(authUserExists === false && localUserExists === false && model.email)"
                            v-if="!physicianId"
                            :dense="denseFields"
                            :width="300"
                            @click="checkEmail"
                            :height="denseFields ? 40 : 48"
                            :label="$t('buttons.check_email.title')">
                      <v-icon left dark>search</v-icon>
                      <span>{{ $t('buttons.check_email.value') }}</span>
                    </lx-btn>
                  </div>

                  <!-- alert when recent auth user (email exists at lx-auth), only when new user -->

                  <div v-show="!physicianId && authUserExists && !localUserExists" class="ml-2 pt-4 flex-100">
                    <div class="layout-row wrap">
                      <div class="flex xs12">
                        <lx-alert-box color="orange"
                                      class="layout wrap flex-gt-sm-100 flex-100 px-2 mt-1 py-1">
                          <div class="layout-row layout-align-start-center flex">
                            <div class="layout-column">
                              <span>{{ $t('COMPONENT_USER_DETAILS.AUTH_PHYSICIAN_ALREADY_EXISTS') }}</span>
                              <span style="font-weight: bold;">{{ $t('COMPONENT_USER_DETAILS.AUTH_USER_ALREADY_EXISTS_HINT') }}</span>
                            </div>

                            <div class="flex"></div>

                            <lx-btn
                              color="orange darken-3"
                              depressed
                              classes="mx-3 text-none"
                              @click="addPhysician"
                              width="auto"
                              small
                              :label="$t('buttons.add_physician.value')">
                            </lx-btn>
                          </div>
                        </lx-alert-box>
                      </div>
                    </div>
                  </div>

                  <!-- alert when recent auth & local user (email exists at lx-auth & local), only when new user -->

                  <div v-show="!physicianId && localUserExists" class="ml-2 pt-4 flex-100">
                    <div class="layout-row wrap">
                      <div class="flex xs12">
                        <lx-alert-box color="red"
                                      class="layout wrap flex-gt-sm-100 flex-100 px-2 mt-1 py-1">
                          {{ $t('COMPONENT_USER_DETAILS.LOCAL_USER_ALREADY_EXISTS') }}
                        </lx-alert-box>
                      </div>
                    </div>
                  </div>

                  <!-- new user (email NOT exists at lx-auth) or existing user with disabled fields-->

                  <v-divider v-bind:style="{'border-color':compColor.lightDivider}" class="mx-3 my-6" v-show="physicianId || (authUserExists === false && localUserExists === false && model.email)"></v-divider>

                  <div v-show="physicianId || (authUserExists === false && localUserExists === false && model.email)" class="layout-row layout-wrap flex-100 mt-6">

                    <!-- salutation -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" name="salutation">
                        <v-select
                          name="salutation"
                          :color="compColor.primeColor"
                          v-model="model.salutation"
                          outlined
                          clearable
                          hide-details
                          :dense="denseFields"
                          :disabled="!!physicianId && !model.pending"
                          :label="$t('COMPONENT_ACCOUNT.SALUTATION')"
                          :dark="compColor.isInDarkMode"
                          :items="salutations"
                          :menu-props="{ bottom: true, offsetY: true }">
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-on="on"
                                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                              <v-list-item-content class="py-0">
                                <label style="cursor:pointer;"
                                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                       class="lighten-4 text--darken-2">{{ item.text }}</label>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                      </validation-provider>
                    </div>

                    <!-- title -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" name="title">
                        <v-select
                          name="title"
                          :color="compColor.primeColor"
                          v-model="model.title"
                          outlined
                          hide-details
                          clearable
                          :dense="denseFields"
                          :disabled="!!physicianId && !model.pending"
                          :label="$t('COMPONENT_ACCOUNT.TITLE')"
                          :dark="compColor.isInDarkMode"
                          :items="titles"
                          :menu-props="{ bottom: true, offsetY: true }">
                          <template v-slot:item="{ item, on, attrs }">
                            <v-list-item v-on="on"
                                         :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                              <v-list-item-content class="py-0">
                                <label style="cursor:pointer;"
                                       :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                       class="lighten-4 text--darken-2">{{ item.text }}</label>
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-select>
                      </validation-provider>
                    </div>

                    <!-- firstname -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" name="firstname" :rules="{required: !physicianId, min: 2, name_string: true}" v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :dense="denseFields"
                          :label="$t('COMPONENT_ACCOUNT.FIRSTNAME') + (isNameEditable ? ' *' : '')"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :disabled="!isNameEditable"
                          :error-messages="errors[0]"
                          v-model="model.firstname"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <!-- lastname -->

                    <div class="flex-gt-sm-50 flex-100 px-2">
                      <validation-provider tag="div" name="lastname" :rules="{required: !physicianId, min: 2, name_string: true}" v-slot="{ errors }">
                        <v-text-field
                          :dark="compColor.isInDarkMode"
                          :color="compColor.primeColor"
                          :dense="denseFields"
                          :label="$t('COMPONENT_ACCOUNT.LASTNAME') + (isNameEditable ? ' *' : '')"
                          outlined
                          :hide-details="!errors || errors.length <= 0"
                          :disabled="!isNameEditable"
                          :error-messages="errors[0]"
                          v-model="model.lastname"
                        ></v-text-field>
                      </validation-provider>
                    </div>

                    <v-divider v-bind:style="{'border-color':compColor.lightDivider}" class="mx-3 my-6" v-show="physicianId || (authUserExists === false && localUserExists === false && model.email)"></v-divider>

                    <!-- lanr -->

                    <div class="flex-gt-sm-100 flex-100 layout-row">
                      <div class="flex-100 px-2">
                        <validation-provider tag="div"
                                             name="lanr"
                                             :rules="{either_lanr_or_iknr:[model.iknr, 'IKNR'],min:9,max:9,lanr: true, decimal: true}"
                                             v-slot="{ errors }">
                          <v-text-field
                            :dark="compColor.isInDarkMode"
                            :color="compColor.primeColor"
                            :dense="denseFields"
                            :label="$t('COMPONENT_PHYSICIAN_DETAILS.LANR')"
                            outlined
                            :hide-details="!errors || errors.length <= 0"
                            :disabled="!!physicianId && !model.pending"
                            maxLength="9"
                            :error-messages="errors[0]"
                            v-model="model.lanr"
                          ></v-text-field>
                        </validation-provider>
                      </div>

                      <div class="mx-3 pt-2" :class="compColor.primeClass">{{ $t('OR') }}</div>

                      <div class="flex-100 px-2">
                          <validation-provider tag="div"
                                               name="iknr"
                                               :rules="{either_lanr_or_iknr:[model.lanr, 'LANR'],min:9,max:9, decimal: true}"
                                               v-slot="{ errors }">
                              <v-text-field
                                      :dark="compColor.isInDarkMode"
                                      :color="compColor.primeColor"
                                      :dense="denseFields"
                                      :label="$t('COMPONENT_PHYSICIAN_DETAILS.IK_NUMBER')"
                                      outlined
                                      :hide-details="!errors || errors.length <= 0"
                                      :disabled="!!physicianId && !model.pending"
                                      maxLength="9"
                                      :error-messages="errors[0]"
                                      v-model="model.iknr"
                              ></v-text-field>
                          </validation-provider>
                      </div>
                    </div>

                    <!-- active state -->

                    <div class="flex-gt-sm-100 flex-100 px-2">
                      <div class="pt-2 layout-row flex md12 sm12 xs12">
                        <label class="pr-2 pt-3" style="font-size: 16px;" :style="{color: compColor.primeColor}">{{ $t('COMPONENT_ACCOUNT.IS_ACTIVE') }}</label>
                        <v-checkbox :dark="compColor.isInDarkMode"
                                    :color="compColor.primeColor"
                                    class="mt-0 py-0"
                                    @change="changeActiveState"
                                    v-model="model.is_active">
                        </v-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>

            <!-- address -->

            <lx-expansion-panel disabled v-show="!!physicianId || (authUserExists === false && localUserExists === false && model.email)">
              <lx-expansion-panel-header icon="mdi-home-variant"
                                         disabled
                                         :main-headline="$t('COMPONENT_ADDRESS_DETAILS.HEADER')"
                                         :sub-headline="$t('COMPONENT_ADDRESS_DETAILS.HEADER_SUB_LINE')">
              </lx-expansion-panel-header>
              <lx-expansion-panel-content>
                <div class="pa-4 layout-row layout-wrap">
                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" :name="'address.street'" rules="min:2" v-slot="{ errors }">
                      <v-text-field
                        :dark="compColor.isInDarkMode"
                        :color="compColor.primeColor"
                        :dense="denseFields"
                        :disabled="!!physicianId && !model.pending"
                        :label="$t('COMPONENT_ADDRESS_DETAILS.STREET')"
                        outlined
                        :hide-details="!errors || errors.length <= 0"
                        :error-messages="errors[0]"
                        v-model="model.address.street"
                      ></v-text-field>
                    </validation-provider>
                  </div>

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" :name="'address.number'" rules="" v-slot="{ errors }">
                      <v-text-field
                        :dark="compColor.isInDarkMode"
                        :color="compColor.primeColor"
                        :dense="denseFields"
                        :disabled="!!physicianId && !model.pending"
                        :label="$t('COMPONENT_ADDRESS_DETAILS.NUMBER')"
                        outlined
                        :hide-details="!errors || errors.length <= 0"
                        :error-messages="errors[0]"
                        v-model="model.address.number"
                      ></v-text-field>
                    </validation-provider>
                  </div>

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" :name="'address.city'" rules="min:2" v-slot="{ errors }">
                      <v-text-field
                        :dark="compColor.isInDarkMode"
                        :color="compColor.primeColor"
                        :dense="denseFields"
                        :disabled="!!physicianId && !model.pending"
                        :label="$t('COMPONENT_ADDRESS_DETAILS.CITY')"
                        outlined
                        :hide-details="!errors || errors.length <= 0"
                        :error-messages="errors[0]"
                        v-model="model.address.city"
                      ></v-text-field>
                    </validation-provider>
                  </div>

                  <div class="flex-gt-sm-50 flex-100 px-2">
                    <validation-provider tag="div" :name="'address.zip'" rules="min:5|max:5|address_string|zip_string" v-slot="{ errors }">
                      <v-text-field
                        :dark="compColor.isInDarkMode"
                        :color="compColor.primeColor"
                        :dense="denseFields"
                        :disabled="!!physicianId && !model.pending"
                        :label="$t('COMPONENT_ADDRESS_DETAILS.ZIP')"
                        outlined
                        :hide-details="!errors || errors.length <= 0"
                        :error-messages="errors[0]"
                        v-model="model.address.zip"
                      ></v-text-field>
                    </validation-provider>
                  </div>
                </div>
              </lx-expansion-panel-content>
            </lx-expansion-panel>
          </lx-expansion-panels>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'

export default {
  name: 'PhysicianDetails',
  props: ['physicianId', 'tabid'],
  data () {
    return {
      e1: true,
      loading: false,
      model: {
        auth_user_id: '',
        pending: true,
        address: {}
      },
      oldModel: {},
      panel: [0, 1],
      error: {},
      errorDialog: false,
      salutations: [
        { value: 'Herr', text: this.$t('COMPONENT_ACCOUNT.SALUTATIONS.MR') },
        { value: 'Frau', text: this.$t('COMPONENT_ACCOUNT.SALUTATIONS.MRS') },
        { value: 'Unbekannt', text: this.$t('COMPONENT_ACCOUNT.SALUTATIONS.UNDEFINED') }
      ],
      titles: [
        { value: 'Dr.', text: 'Dr.' },
        { value: 'Prof.', text: 'Prof.' },
        { value: 'Prof. Dr.', text: 'Prof. Dr.' }
      ],
      alert: false,
      alertMsg: '',
      authUserExists: null,
      localUserExists: null
    }
  },
  created () {
    if (this.physicianId) {
      this.getUserData()
    } else {
      this.model.is_active = true
      this.oldModel = lodash.cloneDeep(this.model)
    }
  },
  methods: {
    /**
     * Reset form
     */
    clearForm (reset) {
      reset() // reset validation
      this.model = lodash.cloneDeep(this.oldModel)

      // reset flags to hide alerts if exists
      this.authUserExists = null
      this.localUserExists = null
    },

    /**
     * Check if email already exists in auth and/or application
     */
    checkEmail () {
      if (!this.physicianId && this.model.email) {
        this.loading = true

        this.$http.get('/physicians/checkAuthUserExists/' + this.model.email)
          .then((res) => {
            if (res.data) {
              this.$set(this, 'authUserExists', res.data.authUser)
              this.$set(this, 'localUserExists', res.data.localUser)
            }
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.loading = false
          })
      }
    },

    /**
     * Add user to application if auth-user already exists
     */
    addPhysician () {
      if (!this.physicianId && this.model.email) {
        if (this.loading) return
        this.loading = true

        this.$http.post('/physicians/addExistingAuthUser', { email: this.model.email, userType: 'physician' })
          .then(res => {
            // remove current tab from store
            this.removeTabFromStoreByTabid(this.tabid)

            if (res.data && res.data.id) {
              // set new tab with saved physician
              this.addTabToStore({
                routeName: 'PhysicianDetails',
                routeParams: { physicianId: res.data.id },
                options: {
                  headline: this.model.email,
                }
              })
            }
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.loading = false
          })
      }
    },

    /**
     * Disable or enable already existing physician for application
     */
    changeActiveState () {
      if (this.physicianId) {
        // update is_active state for user
        if (this.loading) return
        this.loading = true

        this.$http.patch('/physicians/changeActiveState', { user_id: this.physicianId, is_active: this.model.is_active })
          .then(res => {
            this.NotifySuccess(res, this.$t(this.model.is_active ? 'COMPONENT_ACCOUNT.USER_SUCCESSFULLY_ACTIVATED' : 'COMPONENT_ACCOUNT.USER_SUCCESSFULLY_DEACTIVATED'))
          })
          .catch(error => {
            this.model.is_active = !this.model.is_active
            this.NotifyError(error)
          })
          .finally(() => {
            this.loading = false
          })
      }
    },

    /**
     * Save new physician to auth and users
     */
    savePhysicianData () {
      if (this.loading) return
      this.loading = true

      if (!this.physicianId) {
        // create auth user
        const data = {
          email: this.model.email,
          login_name: this.model.login_name,
          password: this.model.password,
          is_active: this.model.is_active,
          firstname: this.model.firstname,
          lastname: this.model.lastname,
          lanr: this.model.lanr,
          iknr: this.model.iknr,
          salutation: this.model.salutation,
          title: this.model.title,
          address: this.model.address,
          user_type: 'physician'
        }

        this.$http.post('/physicians', data)
          .then(res => {
            // remove current tab from store
            this.removeTabFromStoreByTabid(this.tabid)

            if (res.data && res.data.id) {
              // set new tab with saved physician
              this.addTabToStore({
                routeName: 'PhysicianDetails',
                routeParams: { physicianId: res.data.id },
                options: {
                  headline: res.data.lastname + ', ' + res.data.firstname,
                }
              })
            }
          })
          .catch((error) => {
            this.populateFormError(this.$refs.PhysicianDetailForm, error)
              .catch(this.NotifyError)
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        // update user
        const data = {
          id: this.physicianId,
          lastname: this.model.lastname,
          firstname: this.model.firstname,
          salutation: this.model.salutation,
          title: this.model.title,
          address: this.model.address,
          lanr: this.model.lanr,
          iknr: this.model.iknr,
          is_active: this.model.is_active,
          login_name: this.model.login_name
        }

        this.$http.put('/physicians/pending', data)
          .then(() => {
            this.oldModel = lodash.cloneDeep(this.model)

            if (this.$refs.PhysicianDetailForm) {
              this.$refs.PhysicianDetailForm.reset()
            }
          })
          .catch(this.NotifyError)
          .finally(() => {
            this.loading = false
          })
      }
    },

    /**
     * Get data of existing physician
     */
    getUserData () {
      if (this.loading) return
      this.loading = true

      this.$http.get('/physicians/id/' + this.physicianId)
        .then(res => {
          if (res.data) {
            this.model = res.data
            this.oldModel = lodash.cloneDeep(this.model)
          }
        })
        .catch(this.NotifyError)
        .finally(() => {
          this.loading = false
        })
    }
  },
  watch: {
    isPristine (newValue, oldValue) {
      if (!newValue && oldValue) {
        this.$nextTick(() => {
          this.updateTab({ id: this.tabid, show: true })
        })
      }
      if (newValue && !oldValue) {
        this.$nextTick(() => {
          this.updateTab({ id: this.tabid, show: false })
        })
      }
    }
  },
  computed: {
    isMailInvalid () {
      return this.$refs.PhysicianDetailForm.errors && this.$refs.PhysicianDetailForm.errors.email && this.$refs.PhysicianDetailForm.errors.email.length > 0
    },
    isPristine () {
      if (!this.oldModel) {
        return false
      }
      return JSON.stringify(this.oldModel) === JSON.stringify(this.model)
    },
    isNameEditable () {
      return this.model.pending && (!this.model.auth_user_id || this.model.auth_user_id.indexOf('@') === -1)
    }
  }
}
</script>
